<template>
  <q-page class="legal-notice-page">
    <KdsNav
      :logo-label="t('app.name')"
      :links="navLinks"
      :cta-secondary="t('landing.nav.logIn')"
      @cta-secondary-click="goAuth"
    />

    <div class="legal-notice-page__inner">
      <!-- Hero -->
      <header class="legal-notice-page__hero">
        <KdsEyebrow>{{ t('legalNotice.eyebrow') }}</KdsEyebrow>
        <h1 class="legal-notice-page__title">{{ t('legalNotice.title') }}</h1>
      </header>

      <LegalDocsLayout :anchors="legalNoticeAnchors">
        <!-- Section 1 — Site publisher -->
        <section id="section-1" class="legal-notice-page__section" data-reveal>
          <h2 class="legal-notice-page__section-title">{{ t('legalNotice.section1.title') }}</h2>
          <p class="legal-notice-page__section-body">{{ t('legalNotice.section1.intro') }}</p>
          <ul class="legal-notice-page__list">
            <li
              v-for="(item, i) in tm('legalNotice.section1.items')"
              :key="i"
              class="legal-notice-page__list-item"
            >{{ rt(item) }}</li>
          </ul>
        </section>

        <!-- Section 2 — Publication director -->
        <section id="section-2" class="legal-notice-page__section legal-notice-page__section--bordered" data-reveal>
          <h2 class="legal-notice-page__section-title">{{ t('legalNotice.section2.title') }}</h2>
          <p class="legal-notice-page__section-body">{{ t('legalNotice.section2.body') }}</p>
        </section>

        <!-- Section 3 — Hosting -->
        <section id="section-3" class="legal-notice-page__section legal-notice-page__section--bordered" data-reveal>
          <h2 class="legal-notice-page__section-title">{{ t('legalNotice.section3.title') }}</h2>
          <p class="legal-notice-page__section-body">{{ t('legalNotice.section3.intro') }}</p>
          <ul class="legal-notice-page__list">
            <li
              v-for="(item, i) in tm('legalNotice.section3.items')"
              :key="i"
              class="legal-notice-page__list-item"
            >{{ rt(item) }}</li>
          </ul>
          <p class="legal-notice-page__section-body">{{ t('legalNotice.section3.outro') }}</p>
        </section>

        <!-- Section 4 — Intellectual property -->
        <section id="section-4" class="legal-notice-page__section legal-notice-page__section--bordered" data-reveal>
          <h2 class="legal-notice-page__section-title">{{ t('legalNotice.section4.title') }}</h2>
          <p
            v-for="(paragraph, i) in tm('legalNotice.section4.paragraphs')"
            :key="i"
            class="legal-notice-page__section-body"
          >{{ rt(paragraph) }}</p>
        </section>

        <!-- Section 5 — Credits -->
        <section id="section-5" class="legal-notice-page__section legal-notice-page__section--bordered" data-reveal>
          <h2 class="legal-notice-page__section-title">{{ t('legalNotice.section5.title') }}</h2>
          <ul class="legal-notice-page__list">
            <li
              v-for="(item, i) in tm('legalNotice.section5.items')"
              :key="i"
              class="legal-notice-page__list-item"
            >{{ rt(item) }}</li>
          </ul>
        </section>

        <!-- Section 6 — Contact -->
        <section id="section-6" class="legal-notice-page__section legal-notice-page__section--bordered" data-reveal>
          <h2 class="legal-notice-page__section-title">{{ t('legalNotice.section6.title') }}</h2>
          <ul class="legal-notice-page__list">
            <li
              v-for="(item, i) in tm('legalNotice.section6.items')"
              :key="i"
              class="legal-notice-page__list-item"
            >{{ rt(item) }}</li>
          </ul>
        </section>
      </LegalDocsLayout>
    </div>

    <KdsFooter
      :brand="t('app.name')"
      :status-label="t('landing.footer.status')"
      :status-to="'/status'"
      :columns="footerColumns"
    />
  </q-page>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import KdsNav from '@/kds/components/KdsNav.vue';
import KdsFooter from '@/kds/components/KdsFooter.vue';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import LegalDocsLayout from '@/components/static/LegalDocsLayout.vue';
import { useRevealOnScroll } from '@/composables/useRevealOnScroll';
import { useConsent } from '@/composables/useConsent';
import { useSeoMeta } from '@/composables/useSeoMeta';

const { t, tm, rt } = useI18n();
useSeoMeta('legal-notice');
useRevealOnScroll();
const router = useRouter();
const { openPreferences } = useConsent();

const navLinks = computed(() => [
  { label: t('landing.nav.product'), href: '/#how' },
  { label: t('landing.nav.protocols'), href: '/#protocols' },
  { label: t('landing.nav.pricing'), href: '/#pricing' },
  { label: t('landing.nav.docs'), href: '/docs' },
  { label: t('landing.nav.changelog'), href: '/changelogs' }
]);

const legalNoticeAnchors = computed(() => [
  { label: t('legalNotice.section1.title'), anchor: 'section-1' },
  { label: t('legalNotice.section2.title'), anchor: 'section-2' },
  { label: t('legalNotice.section3.title'), anchor: 'section-3' },
  { label: t('legalNotice.section4.title'), anchor: 'section-4' },
  { label: t('legalNotice.section5.title'), anchor: 'section-5' },
  { label: t('legalNotice.section6.title'), anchor: 'section-6' }
]);

const footerColumns = computed(() => [
  {
    title: t('landing.footer.productTitle'),
    links: [
      { label: t('landing.footer.linkOverview'), href: '/#how' },
      { label: t('landing.footer.linkProtocols'), href: '/#protocols' },
      { label: t('landing.footer.linkPricing'), href: '/#pricing' }
    ]
  },
  {
    title: t('landing.footer.resourcesTitle'),
    links: [
      { label: t('landing.footer.linkDocs'), to: '/docs' },
      { label: t('landing.footer.linkChangelog'), to: '/changelogs' },
      { label: t('landing.footer.linkStatus'), to: '/status' }
    ]
  },
  {
    title: t('landing.footer.companyTitle'),
    links: [
      { label: t('landing.footer.linkAbout'), to: '/about' },
      { label: t('landing.footer.linkContact'), to: { path: '/about', hash: '#contact' } }
    ]
  },
  {
    title: t('landing.footer.legalTitle'),
    links: [
      { label: t('landing.footer.linkPrivacy'), to: '/privacy' },
      { label: t('landing.footer.linkTerms'), to: '/terms' },
      { label: t('landing.footer.linkCookies'), to: '/cookies' },
      { label: t('landing.footer.linkLegalNotice'), to: '/legal-notice' },
      { label: t('landing.footer.manageCookies'), action: openPreferences }
    ]
  }
]);

const goAuth = (): void => {
  void router.push({ name: 'auth' });
};
</script>

<style scoped lang="scss">
.legal-notice-page {
  background: var(--paper);
  color: var(--ink);

  &__inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: var(--space-11) var(--space-7);
  }

  &__hero {
    margin-bottom: var(--space-9);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: 1.18;
    letter-spacing: -0.015em;
    color: var(--ink);
  }

  &__section {
    padding-top: var(--space-9);
    scroll-margin-top: calc(var(--nav-height, 56px) + var(--space-2));
    display: flex;
    flex-direction: column;
    gap: var(--space-5);

    &--bordered {
      margin-top: var(--space-9);
      border-top: var(--border);
    }
  }

  &__section-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h2);
    line-height: 1.22;
    letter-spacing: -0.012em;
    color: var(--ink);
  }

  &__section-body {
    margin: 0;
    font-size: var(--fs-body);
    line-height: 1.65;
    color: var(--stone);

    a {
      color: var(--ink);
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  &__list {
    margin: 0;
    padding-left: var(--space-6);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  &__list-item {
    font-size: var(--fs-body);
    line-height: 1.65;
    color: var(--stone);
  }
}
</style>
